import { logger } from '@/lib/logger';
import { getActiveCaregiverId } from '@lib/activeOffice';
import keycloakIns from '@lib/keycloak';
import { CURRENT_OFFICE_LOCATION, EXERCISE_BASKET } from '@src/const/sessionStorageConsts';
import { clearDraftBuffers } from '@/stores/draftSessionStorage';
import { useActivityPointSocketStore } from '@stores/activityPointSocketStore';
import { useCallSocketStore } from '@stores/call/callSocketStore';
import { useCallStore } from '@stores/call/callStore';
import { useModalStore } from '@stores/modalStore';
import { useNotificationSocketStore } from '@stores/notificationSocketStore';
import { useOfficeAchievementSocketStore } from '@stores/officeAchievementSocketStore';
import { usePushNotificationStore } from '@stores/pushNotificationStore';
import { useUserSettingsStore } from '@stores/userSettingsStore';
import { useQueryClient } from '@tanstack/vue-query';
import {
  legalDocumentsKeys,
  legalDocumentsQueries,
  usersKeys,
  usersQueries,
} from '@therame/api-client';
import {
  AccountOfficeDto,
  AuthContext,
  FeatureFlagFullSizeDto,
  FeatureFlagType,
  LegalDocument,
  RegistrationIntentDto,
  Role,
} from '@therame/shared';
import { useAuthContext } from '@therame/ui-domain';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import { useTourStore } from './Tours/tourStore';
import { useDiagnosisStore } from './diagnosisStore';
import { useFeatureToggleStore } from './featureToggleStore';
import { useOfficeLocationStore } from './officeLocationStore';

type AuthStore = {
  authContext: Ref<AuthContext | undefined>;
  /** The Account's offices, from `GET /auth/offices` at bootstrap (#1822). */
  accountOffices: Ref<AccountOfficeDto[]>;
  /** Several offices and no valid stored choice: only „Praxis wählen“ is reachable. */
  officeChoicePending: Ref<boolean>;
  /** The Account has no Person a session could act as: only „Praxis eröffnen“ is offered (#1832). */
  accountWithoutOffice: Ref<boolean>;
  /**
   * What such an Account registered for, while its first step hasn't consumed
   * it (#1833): `ADMIN` goes straight into „Praxis eröffnen“, `PATIENT` to the
   * join page of its registration link (#1834).
   */
  registrationRole: Ref<RegistrationIntentDto['role']>;
  /** That registration link; `null` when it is gone, and for every other role. */
  registrationLinkToken: Ref<RegistrationIntentDto['registrationLinkToken']>;
  /** What the Person of the Active office still has to accept; only the acceptance step is reachable meanwhile (#1824). */
  pendingLegalDocuments: Ref<LegalDocument[]>;
  featureFlags: Ref<FeatureFlagFullSizeDto[]>;
  login: () => Promise<void>;
  logout: (noAjaxCall?: boolean) => Promise<void>;
  refreshAuthContext: () => Promise<void>;
  isLatestVersion: () => boolean;
  /**
   * Whether the subject Person is past onboarding, as far as the app is
   * concerned: onboarded, or acted for through a caregiver entry (#1841) —
   * onboarding is the first step of a Person's own login, and a child the
   * office manages has none.
   */
  subjectOnboarded: () => boolean;
  isFeatureFlagEnabled: (feature: FeatureFlagType) => boolean;
};

export const useAuthStore = defineStore('auth/user', (): AuthStore => {
  const featureFlags = ref<FeatureFlagFullSizeDto[]>([]);
  const accountOffices = ref<AccountOfficeDto[]>([]);
  const officeChoicePending = ref(false);
  const accountWithoutOffice = ref(false);
  const registrationRole = ref<RegistrationIntentDto['role']>(null);
  const registrationLinkToken = ref<RegistrationIntentDto['registrationLinkToken']>(null);
  const pendingLegalDocuments = ref<LegalDocument[]>([]);
  const callSocketStore = useCallSocketStore();
  const notificationSocketStore = useNotificationSocketStore();
  const activityPointSocketStore = useActivityPointSocketStore();
  const officeAchievementSocketStore = useOfficeAchievementSocketStore();
  const modalStore = useModalStore();
  const tourStore = useTourStore();
  const callStore = useCallStore();

  const userSettingsStore = useUserSettingsStore();
  const pushNotificationStore = usePushNotificationStore();
  const featureToggleStore = useFeatureToggleStore();
  const queryClient = useQueryClient();

  // From composables/useAuthContext in ui
  const { updateAuthContext, authContext } = useAuthContext();

  const login = async () => {
    const diagnosisStore = useDiagnosisStore();
    diagnosisStore.reset();
    pushNotificationStore.resetPushNotificationState();

    await refreshAuthContext();
    await loadPendingLegalDocuments();

    if (authContext.value?.user?.role === Role.PATIENT && authContext.value?.usedForm !== 'tan') {
      diagnosisStore.updateDiagnosisFormsCompleted();
    }

    // Defer socket.io connections until the browser is idle. They compete with
    // the initial dashboard queries (e.g. patients-with-training-session-warnings)
    // for the per-origin connection pool, which otherwise delays critical data by
    // ~1s on cold load. `requestIdleCallback` has a 3s timeout fallback; older
    // Safari falls back to setTimeout.
    const connectSockets = () => {
      initVideoCallConnection();
      // The notifications gateway takes a bearer only; a TAN tab joins its call.
      if (authContext.value?.usedForm !== 'tan') notificationSocketStore.connect();
      if (authContext.value?.user?.role === Role.PATIENT && authContext.value?.usedForm !== 'tan') {
        activityPointSocketStore.connect();
      }
      if (authContext.value?.user?.role === Role.ADMIN) {
        officeAchievementSocketStore.connect();
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(connectSockets, { timeout: 3000 });
    } else {
      setTimeout(connectSockets, 1500);
    }
  };

  const logout = async (noAjaxCall?: boolean) => {
    if (keycloakIns.authenticated && !noAjaxCall) {
      if (callStore.callId) {
        await callStore.hangUp();
      }
      modalStore.closeAllModals();
      callSocketStore.disconnectSocket();
      notificationSocketStore.disconnectSocket();
      // Explicit logout only: drop this device's push subscription so the next
      // user on this browser doesn't inherit it. Runs while the auth context and
      // token are still valid, so the backend delete is authenticated. The
      // session-expiry path (noAjaxCall) deliberately keeps the subscription — the
      // next login is asked whether to take it over instead.
      //
      // Best-effort: PushManager can reject (getSubscription/unsubscribe throwing
      // on a broken or restricted SW). A failed push cleanup must never abort the
      // mandatory cleanup below or in the caller — callers clear the access/refresh
      // tokens and challenge_* bootstrap state after us, and skipping that would
      // leave stale credentials behind across the logout redirect.
      try {
        await pushNotificationStore.unsubscribePush();
      } catch (error) {
        logger.error({ msg: 'Failed to unsubscribe push during logout', error });
      }
    }
    clearSessionStorage();
    authContext.value = undefined;
    pushNotificationStore.resetPushNotificationState();
  };

  const subjectOnboarded = () =>
    !!authContext.value?.user?.isOnboarded || getActiveCaregiverId() !== null;

  /**
   * Single source of truth for refreshing the auth context from /users/me.
   * Called by login() on app init and by all callers that need fresh auth state
   * (e.g. after Stripe entitlements update, office profile changes, etc.).
   */
  const refreshAuthContext = async () => {
    const officeLocationStore = useOfficeLocationStore();

    const userInfo = await queryClient.fetchQuery({
      queryKey: usersKeys.me(),
      queryFn: usersQueries.getCurrentUser,
      staleTime: 0,
    });
    tourStore.tourStatus = userInfo.tourStatus as any; // TODO: fix typing
    tourStore.startTourTilesEnabled = dayjs(userInfo.user.registeredAt)
      .add(30, 'day')
      .isAfter(dayjs());

    featureFlags.value = (userInfo.featureFlags as any) || []; // TODO: fix typing

    updateAuthContext(userInfo.user as any);
    const isOnboardedOrTan = subjectOnboarded() || authContext.value?.usedForm === 'tan';
    if (!isOnboardedOrTan) return;

    if (authContext.value?.usedForm !== 'tan') {
      userSettingsStore.loadSettings(userInfo.settings as any); // TODO: fix typing
    }

    // Accounts without a practice have no locations to load: the SUPERADMIN has
    // never had any, the Versand-Operator since #1612.
    if (
      authContext.value?.user.role === Role.SUPERADMIN ||
      authContext.value?.user.role === Role.TRANSMISSION_OPERATOR
    ) {
      return;
    }

    officeLocationStore.allOfficeLocations = userInfo.officeLocations as any; // TODO: fix typing
    const newOfficeLocation =
      officeLocationStore.allOfficeLocations.find(
        (officeLocation) => officeLocation.id === authContext.value?.user.currentOfficeLocationId,
      ) || officeLocationStore.allOfficeLocations[0];
    if (authContext.value && newOfficeLocation) {
      authContext.value = AuthContext.setCurrentOfficeLocation(
        authContext.value,
        newOfficeLocation,
      );
    }

    if (newOfficeLocation?.id && authContext.value?.usedForm !== 'tan') {
      featureToggleStore.loadFeatureToggles(newOfficeLocation.id);
    }
  };

  /**
   * Legal acceptances are gated in the app, per Person of the Active office
   * (#1824). A TAN session joins one call and is asked nothing.
   */
  const loadPendingLegalDocuments = async () => {
    if (!authContext.value?.user || authContext.value.usedForm === 'tan') {
      pendingLegalDocuments.value = [];
      return;
    }
    const { documents } = await queryClient.fetchQuery({
      queryKey: legalDocumentsKeys.pending(),
      queryFn: legalDocumentsQueries.getPending,
      staleTime: 0,
    });
    pendingLegalDocuments.value = documents;
  };

  const clearSessionStorage = () => {
    sessionStorage.removeItem(EXERCISE_BASKET);
    sessionStorage.removeItem(CURRENT_OFFICE_LOCATION);
    // Reload copies of Entwürfe hold patient data; none outlive the session (TM-9664).
    clearDraftBuffers();
  };

  const isLatestVersion = () => {
    return (
      authContext.value?.user.lastVersion === APP_VERSION ||
      import.meta.env.VITE_MODE === 'TESTING-FRONTEND'
    );
  };

  const isFeatureFlagEnabled = (feature: FeatureFlagType) => {
    return featureFlags.value.some((ff) => ff.type === feature);
  };

  const initVideoCallConnection = async () => {
    if (
      authContext.value &&
      [Role.PREVENTION, Role.MANAGEMENT, Role.SUPERADMIN, Role.TRANSMISSION_OPERATOR].includes(
        authContext.value.user.role,
      )
    ) {
      return;
    }
    // A caregiver may book a video appointment for the patient, but only the
    // patient joins the call: the backend refuses the calls socket here.
    if (getActiveCaregiverId() !== null) {
      return;
    }
    callStore.initSubscriptions();
    callSocketStore.connect();
  };

  return {
    authContext,
    accountOffices,
    officeChoicePending,
    accountWithoutOffice,
    registrationRole,
    registrationLinkToken,
    pendingLegalDocuments,
    featureFlags,
    login,
    logout,
    refreshAuthContext,
    isLatestVersion,
    subjectOnboarded,
    isFeatureFlagEnabled,
  };
});
